import { makeObservable, observable, computed, action } from 'mobx'
import dayjs from 'dayjs'
/**
 * Class representing a Documentation item.
 * Contains additional properties and methods specific to Documentation items.
 *
 * @class Documentation
 */
export class Documentation {
	_class = 'Documentation'
	documentation_id = ''
	name = ''
	description = ''
	body = ''
	appType = ''
	created_by = ''
	created_at = ''
	modified = ''
	to_delete = false

	/**
	 * Creates an instance of Documentation.
	 * @param {string} id - The unique identifier for the Documentation item.
	 */
	constructor(id) {
		this.documentation_id = id

		makeObservable(this, {
			documentation_id: observable,

			name: observable,
			setName: action.bound,

			description: observable,
			setDescription: action.bound,

			body: observable,
			setBody: action.bound,

			appType: observable,
			setAppType: action.bound,

			created_by: observable,
			setCreatedBy: action.bound,

			created_at: observable,
			createdTs: computed,

			to_delete: observable,
			setToDelete: action.bound,

			modifiedTs: computed,
			setModified: action.bound,

			updateFromServer: action
		})
	}

	/**
	 * Sets the name of the documentation.
	 *
	 * @param {string} value - The name of the documentation to set.
	 */
	setName(value) {
		this.name = value
	}

	/**
	 * Sets the description of the documentation.
	 *
	 * @param {string} value - The description of the documentation to set.
	 */
	setDescription(value) {
		this.description = value
	}

	/**
	 * Sets the body content of the documentation.
	 *
	 * @param {string} value - The body content of the documentation to set.
	 */
	setBody(value) {
		this.body = value
	}

	/**
	 * Sets the appType of the documentation.
	 *
	 * @param {string} value - The appType of the documentation to set.
	 */
	setAppType(value) {
		this.appType = value
	}

	/**
	 * Sets the ID of the user who created the documentation.
	 *
	 * @param {string} value - The ID of the user to set as the creator.
	 */
	setCreatedBy(value) {
		this.created_by = value
	}

	/**
	 * Sets the deletion status of the documentation.
	 *
	 * @param {boolean} value - True if the documentation is marked for deletion, false otherwise.
	 */
	setToDelete(value) {
		this.to_delete = value
	}

	/**
	 * Gets the creation timestamp of the documentation as a Unix timestamp.
	 *
	 * @returns {number} The creation time in Unix format.
	 */
	get createdTs() {
		return dayjs(this.created_at).unix()
	}

	// modified comes from the server, added using new Date(), and it comes in the format 2024-08-29 11:12:48. I need to convert it to timestamp
	get modifiedTs() {
		return dayjs(this.modified).unix()
	}

	setModified(modified) {
		this.modified = modified
	}

	/**
	 * Updates the item with the data from the server.
	 * @param {object} srvJson - The server data.
	 */
	updateFromServer(srvJson) {
		this.documentation_id = srvJson.documentation_id
		this.name = srvJson.name
		this.description = srvJson.description
		this.body = srvJson.body
		this.appType = srvJson.appType
		this.created_by = srvJson.created_by
		this.created_at = srvJson.created_at
		this.modified = srvJson?.modified || null

		// Due to special nature of deletion based on progress, we will convert the date to unix here
		this.to_delete = srvJson.to_delete
			? dayjs(srvJson.to_delete).unix()
			: false
	}
}
